import React, { useEffect, useState, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Table,
  Button,
  Card,
  Input,
  Space,
  Typography,
  Spin,
  DatePicker,
  Popconfirm,
  Tag,
  Tooltip,
  Popover,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CalendarOutlined,
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  BankOutlined,
  FilterOutlined,
  TagOutlined,
  DownOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useStore } from '../../stores/root-store-context';
import { useNavigate } from 'react-router';
import CrmUserStamp from '../../components/crm/CrmUserStamp';
import './crm.css';

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

const getEventTypeBadge = (typeName: string) => {
  if (!typeName) return <span style={{ color: '#cbd5e1' }}>—</span>;
  const nameLower = typeName.toLowerCase();
  if (nameLower.includes('дзвін') || nameLower.includes('call') || nameLower.includes('звон')) {
    return (
      <Tag color="success" className="crm-event-tag">
        <PhoneOutlined style={{ marginRight: 4 }} />
        {typeName}
      </Tag>
    );
  }
  if (nameLower.includes('зустріч') || nameLower.includes('meet') || nameLower.includes('встреч')) {
    return (
      <Tag color="purple" className="crm-event-tag">
        <CalendarOutlined style={{ marginRight: 4 }} />
        {typeName}
      </Tag>
    );
  }
  if (nameLower.includes('лист') || nameLower.includes('mail') || nameLower.includes('письм') || nameLower.includes('email')) {
    return (
      <Tag color="blue" className="crm-event-tag">
        <MailOutlined style={{ marginRight: 4 }} />
        {typeName}
      </Tag>
    );
  }
  return <Tag color="default" className="crm-event-tag">{typeName}</Tag>;
};

/* ───────────────────────────────────── Filter Pill Component ─────────────────── */

interface FilterPillDropdownProps {
  icon: React.ReactNode;
  label: string;
  options: { id: number; name: string }[];
  selectedValues: number[];
  onChange: (vals: number[]) => void;
  searchPlaceholder?: string;
  clearLabel?: string;
  emptyLabel?: string;
}

const FilterPillDropdown: React.FC<FilterPillDropdownProps> = ({
  icon,
  label,
  options,
  selectedValues,
  onChange,
  searchPlaceholder,
  clearLabel,
  emptyLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    return options.filter((o) => o.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [options, searchQuery]);

  const toggleValue = (id: number) => {
    if (selectedValues.includes(id)) {
      onChange(selectedValues.filter((x) => x !== id));
    } else {
      onChange([...selectedValues, id]);
    }
  };

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setSearchQuery('');
      }}
      content={
        <div className="crm-pill-dropdown-content">
          {options.length > 4 && (
            <div className="crm-pill-dropdown-search">
              <Input
                size="small"
                placeholder={searchPlaceholder || 'Search...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              />
            </div>
          )}
          <div className="crm-pill-dropdown-list">
            {filteredOptions.length === 0 ? (
              <div className="crm-pill-dropdown-empty">{emptyLabel || 'No results'}</div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selectedValues.includes(opt.id);
                return (
                  <div
                    key={opt.id}
                    className={`crm-pill-dropdown-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleValue(opt.id)}
                  >
                    <Checkbox checked={isSelected} />
                    <span className="crm-pill-dropdown-item-text">{opt.name}</span>
                  </div>
                );
              })
            )}
          </div>
          {selectedValues.length > 0 && (
            <div className="crm-pill-dropdown-footer">
              <Button type="link" size="small" onClick={() => onChange([])}>
                {clearLabel || 'Clear'} ({selectedValues.length})
              </Button>
            </div>
          )}
        </div>
      }
    >
      <button
        type="button"
        className={`crm-filter-pill-btn ${selectedValues.length > 0 ? 'crm-pill-active' : ''}`}
      >
        {icon}
        <span>{label}</span>
        {selectedValues.length > 0 && <span className="crm-pill-count">{selectedValues.length}</span>}
        <DownOutlined
          style={{
            fontSize: 10,
            color: selectedValues.length > 0 ? '#2563eb' : '#94a3b8',
            marginLeft: 2,
          }}
        />
      </button>
    </Popover>
  );
};

/* ───────────────────────────────────── Main Component ───────────────────────── */

const CrmEventManagement: React.FC = observer(() => {
  const { t } = useTranslation();
  const { crmStore, applicationStore } = useStore();
  const navigate = useNavigate();
  const searchInputRef = useRef<any>(null);

  /* ───────────────────────────────────── state ─────────────────────────────────── */

  const [search, setSearch] = useState<string>('');
  const [selectedTypes, setSelectedTypes] = useState<number[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<number[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [datePreset, setDatePreset] = useState<string>('all');
  const [datePopoverOpen, setDatePopoverOpen] = useState<boolean>(false);
  const [quickStatusTab, setQuickStatusTab] = useState<'all' | 'upcoming' | 'today' | 'past'>('all');

  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  /* ───────────────────────────────────── shortcuts ──────────────────────────────── */

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedTypes, selectedContacts, selectedCompanies, dateRange, quickStatusTab]);

  /* ───────────────────────────────────── data loading ───────────────────────────── */

  useEffect(() => {
    Promise.all([
      crmStore.getCrmEvents(true),
      crmStore.getCrmEventTypes(),
      crmStore.getContacts(),
      crmStore.getExternalCompanies(),
    ]);
  }, [crmStore]);

  /* ───────────────────────────────────── counts & filtering ─────────────────────── */

  const now = dayjs();

  const counts = useMemo(() => {
    const all = crmStore.CrmEvents.length;
    const upcoming = crmStore.CrmEvents.filter(
      (e) => dayjs(e.dateStart).isAfter(now) || dayjs(e.dateEnd).isAfter(now)
    ).length;
    const past = crmStore.CrmEvents.filter((e) => dayjs(e.dateEnd).isBefore(now)).length;
    const today = crmStore.CrmEvents.filter(
      (e) => dayjs(e.dateStart).isSame(now, 'day') || dayjs(e.dateEnd).isSame(now, 'day')
    ).length;
    return { all, upcoming, past, today };
  }, [crmStore.CrmEvents]);

  const filteredEvents = useMemo(() => {
    const q = search.toLowerCase().trim();

    return crmStore.CrmEvents.filter((ev) => {
      // 1. Text search
      if (q && !ev.name.toLowerCase().includes(q)) {
        return false;
      }

      // 2. Types
      if (selectedTypes.length > 0 && !selectedTypes.includes(ev.typeId)) {
        return false;
      }

      // 3. Contacts
      if (selectedContacts.length > 0) {
        const hasContact = ev.contacts?.some((c: any) => selectedContacts.includes(c.id));
        if (!hasContact) return false;
      }

      // 4. Companies
      if (selectedCompanies.length > 0) {
        const hasCompany = ev.externalCompanies?.some((c: any) => selectedCompanies.includes(c.id));
        if (!hasCompany) return false;
      }

      // 5. Date range
      if (dateRange && (dateRange[0] || dateRange[1])) {
        const [start, end] = dateRange;
        const evStart = dayjs(ev.dateStart);
        const evEnd = dayjs(ev.dateEnd);
        if (start && evEnd.isBefore(start.startOf('day'))) return false;
        if (end && evStart.isAfter(end.endOf('day'))) return false;
      }

      // 6. Quick status tab
      if (quickStatusTab === 'upcoming') {
        const isUpcoming = dayjs(ev.dateStart).isAfter(now) || dayjs(ev.dateEnd).isAfter(now);
        if (!isUpcoming) return false;
      } else if (quickStatusTab === 'today') {
        const isToday = dayjs(ev.dateStart).isSame(now, 'day') || dayjs(ev.dateEnd).isSame(now, 'day');
        if (!isToday) return false;
      } else if (quickStatusTab === 'past') {
        const isPast = dayjs(ev.dateEnd).isBefore(now);
        if (!isPast) return false;
      }

      return true;
    }).sort((a, b) => b.id - a.id);
  }, [crmStore.CrmEvents, search, selectedTypes, selectedContacts, selectedCompanies, dateRange, quickStatusTab]);

  const rangeStart = filteredEvents.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, filteredEvents.length);

  const hasActiveFilters = Boolean(
    search ||
    selectedTypes.length > 0 ||
    selectedContacts.length > 0 ||
    selectedCompanies.length > 0 ||
    (dateRange && (dateRange[0] || dateRange[1])) ||
    quickStatusTab !== 'all'
  );

  const resetAllFilters = () => {
    setSearch('');
    setSelectedTypes([]);
    setSelectedContacts([]);
    setSelectedCompanies([]);
    setDateRange(null);
    setDatePreset('all');
    setQuickStatusTab('all');
  };

  const datePillLabel = useMemo(() => {
    if (!dateRange || (!dateRange[0] && !dateRange[1])) {
      return t('crm.period', 'Період');
    }
    if (datePreset === 'today') return t('crm.today', 'Сьогодні');
    if (datePreset === 'yesterday') return t('crm.yesterday', 'Вчора');
    if (datePreset === 'this_week') return t('crm.thisWeek', 'Цей тиждень');
    if (datePreset === 'this_month') return t('crm.thisMonth', 'Цей місяць');
    if (datePreset === 'last_7_days') return t('crm.last7Days', 'Останні 7 днів');
    const s = dateRange[0]?.format('DD.MM') || '...';
    const e = dateRange[1]?.format('DD.MM.YYYY') || '...';
    return `${s} — ${e}`;
  }, [dateRange, datePreset, t]);

  /* ───────────────────────────────────── option lists ────────────────────────────── */

  const typeOptions = useMemo(() => {
    return crmStore.CrmEventTypes.map((t) => ({ id: t.id, name: t.name }));
  }, [crmStore.CrmEventTypes]);

  const contactOptions = useMemo(() => {
    return crmStore.contacts.map((c) => ({
      id: c.id,
      name: `${c.name} ${c.surname}`.trim(),
    }));
  }, [crmStore.contacts]);

  const companyOptions = useMemo(() => {
    return crmStore.externalCompanies.map((c) => ({ id: c.id, name: c.name }));
  }, [crmStore.externalCompanies]);

  /* ───────────────────────────────────── columns ─────────────────────────────────── */

  const columns: any[] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: '5%',
      sorter: (a: any, b: any) => a.id - b.id,
      defaultSortOrder: 'descend',
      render: (id: number) => <span className="crm-id-cell">#{id}</span>,
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      width: '22%',
      sorter: (a: any, b: any) => (a.name || '').localeCompare(b.name || ''),
      render: (_: unknown, record: any) => {
        const start = dayjs(record.dateStart);
        const end = dayjs(record.dateEnd);
        let dotColor = '#94a3b8'; // past/default
        if (now.isBetween(start, end, null, '[]') || start.isSame(now, 'day')) {
          dotColor = '#10b981'; // active/today green
        } else if (start.isAfter(now)) {
          dotColor = '#3b82f6'; // upcoming blue
        }

        return (
          <div className="crm-event-name-cell">
            <span className="crm-event-dot" style={{ backgroundColor: dotColor }} />
            <span className="crm-event-name-text" title={record.name}>
              {record.name}
            </span>
          </div>
        );
      },
    },
    {
      title: t('crm.eventSchedule', 'Час проведення'),
      key: 'schedule',
      width: '18%',
      sorter: (a: any, b: any) => new Date(a.dateStart).getTime() - new Date(b.dateStart).getTime(),
      render: (_: unknown, r: any) => {
        const start = dayjs(r.dateStart);
        const end = dayjs(r.dateEnd);
        const isSameDay = start.isSame(end, 'day');
        return (
          <div className="crm-event-schedule-cell">
            <span className="crm-event-date-main">
              <CalendarOutlined style={{ marginRight: 6, color: '#64748b', fontSize: 12 }} />
              {start.format('DD/MM/YYYY')}
            </span>
            <span className="crm-event-time-sub">
              {start.format('HH:mm')} – {end.format(isSameDay ? 'HH:mm' : 'DD/MM/YYYY HH:mm')}
            </span>
          </div>
        );
      },
    },
    {
      title: t('CrmEventType'),
      dataIndex: 'typeId',
      key: 'typeId',
      width: '13%',
      sorter: (a: any, b: any) => {
        const tA = crmStore.CrmEventTypes.find((t) => t.id === a.typeId)?.name || '';
        const tB = crmStore.CrmEventTypes.find((t) => t.id === b.typeId)?.name || '';
        return tA.localeCompare(tB);
      },
      render: (id: number) => {
        const name = crmStore.CrmEventTypes.find((t) => t.id === id)?.name || '';
        return getEventTypeBadge(name);
      },
    },
    {
      title: t('Contacts'),
      dataIndex: 'contacts',
      key: 'contacts',
      width: '15%',
      render: (arr: any[]) => {
        if (!arr || arr.length === 0) return <span style={{ color: '#cbd5e1' }}>—</span>;
        return (
          <Space size={4} wrap onClick={(e) => e.stopPropagation()}>
            {arr.slice(0, 2).map((c: any) => (
              <span
                key={c.id}
                className="crm-company-pill"
                style={{ fontSize: 12, padding: '2px 8px' }}
                onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/contact/${c.id}`)}
                title={`${c.name} ${c.surname}`}
              >
                <UserOutlined style={{ color: '#3b82f6', fontSize: 11 }} />
                <span>{`${c.name} ${c.surname}`.trim()}</span>
              </span>
            ))}
            {arr.length > 2 && (
              <Tooltip title={arr.slice(2).map((c) => `${c.name} ${c.surname}`).join(', ')}>
                <Tag style={{ margin: 0 }}>+{arr.length - 2}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: t('Companies'),
      dataIndex: 'externalCompanies',
      key: 'externalCompanies',
      width: '15%',
      render: (arr: any[]) => {
        if (!arr || arr.length === 0) return <span style={{ color: '#cbd5e1' }}>—</span>;
        return (
          <Space size={4} wrap onClick={(e) => e.stopPropagation()}>
            {arr.slice(0, 2).map((c: any) => (
              <span
                key={c.id}
                className="crm-company-pill"
                style={{ fontSize: 12, padding: '2px 8px' }}
                onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/company/${c.id}`)}
                title={c.name}
              >
                <BankOutlined style={{ color: '#6366f1', fontSize: 11 }} />
                <span>{c.name}</span>
              </span>
            ))}
            {arr.length > 2 && (
              <Tooltip title={arr.slice(2).map((c) => c.name).join(', ')}>
                <Tag style={{ margin: 0 }}>+{arr.length - 2}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: t('CreatedBy'),
      key: 'createdBy',
      width: '11%',
      sorter: (a: any, b: any) => (a.createdAt || '').localeCompare(b.createdAt || ''),
      render: (_: unknown, r: any) => <CrmUserStamp name={r.createdByName} date={r.createdAt} />,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '12%',
      render: (_: unknown, record: any) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button
            className="crm-action-btn crm-action-btn-edit"
            icon={<EditOutlined />}
            onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/event/${record.id}?edit=1`)}
          >
            {t('Edit')}
          </Button>
          <Popconfirm
            title={t('DeleteCrmEventConfirm')}
            onConfirm={() => crmStore.deleteCrmEvent(record.id)}
          >
            <Button
              className="crm-action-btn crm-action-btn-delete"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /* ───────────────────────────────────── render ─────────────────────────────────── */

  return (
    <Spin spinning={crmStore.isLoading}>
      <Card bordered={false} className="crm-events-card">
        {/* Top unified toolbar */}
        <div className="crm-unified-toolbar">
          <div className="crm-toolbar-left-group">
            {/* Search Input with ⌘K */}
            <div className="crm-search-pill-wrapper">
              <Input
                ref={searchInputRef}
                placeholder={t('crm.searchEvents', 'Пошук за назвою...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                suffix={<kbd className="crm-kbd">⌘K</kbd>}
                className="crm-unified-search-input"
              />
            </div>

            {/* Event Type Filter Pill */}
            <FilterPillDropdown
              icon={
                <TagOutlined
                  style={{
                    color: selectedTypes.length > 0 ? '#2563eb' : '#64748b',
                  }}
                />
              }
              label={t('CrmEventType')}
              options={typeOptions}
              selectedValues={selectedTypes}
              onChange={setSelectedTypes}
              searchPlaceholder={t('Search')}
              clearLabel={t('Clean')}
            />

            {/* Contact Filter Pill */}
            <FilterPillDropdown
              icon={
                <UserOutlined
                  style={{
                    color: selectedContacts.length > 0 ? '#2563eb' : '#64748b',
                  }}
                />
              }
              label={t('Contact')}
              options={contactOptions}
              selectedValues={selectedContacts}
              onChange={setSelectedContacts}
              searchPlaceholder={t('Search')}
              clearLabel={t('Clean')}
            />

            {/* Company Filter Pill */}
            <FilterPillDropdown
              icon={
                <BankOutlined
                  style={{
                    color: selectedCompanies.length > 0 ? '#2563eb' : '#64748b',
                  }}
                />
              }
              label={t('Company')}
              options={companyOptions}
              selectedValues={selectedCompanies}
              onChange={setSelectedCompanies}
              searchPlaceholder={t('Search')}
              clearLabel={t('Clean')}
            />

            {/* Date Period Filter Pill with Presets Popover */}
            <Popover
              trigger="click"
              placement="bottomLeft"
              open={datePopoverOpen}
              onOpenChange={setDatePopoverOpen}
              content={
                <div className="crm-date-preset-popover">
                  <div className="crm-date-preset-list">
                    <button
                      type="button"
                      className={`crm-preset-btn ${datePreset === 'all' ? 'active' : ''}`}
                      onClick={() => {
                        setDatePreset('all');
                        setDateRange(null);
                        setDatePopoverOpen(false);
                      }}
                    >
                      {t('crm.all', 'Всі')}
                    </button>
                    <button
                      type="button"
                      className={`crm-preset-btn ${datePreset === 'today' ? 'active' : ''}`}
                      onClick={() => {
                        setDatePreset('today');
                        setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
                        setDatePopoverOpen(false);
                      }}
                    >
                      {t('crm.today', 'Сьогодні')}
                    </button>
                    <button
                      type="button"
                      className={`crm-preset-btn ${datePreset === 'yesterday' ? 'active' : ''}`}
                      onClick={() => {
                        setDatePreset('yesterday');
                        setDateRange([
                          dayjs().subtract(1, 'day').startOf('day'),
                          dayjs().subtract(1, 'day').endOf('day'),
                        ]);
                        setDatePopoverOpen(false);
                      }}
                    >
                      {t('crm.yesterday', 'Вчора')}
                    </button>
                    <button
                      type="button"
                      className={`crm-preset-btn ${datePreset === 'this_week' ? 'active' : ''}`}
                      onClick={() => {
                        setDatePreset('this_week');
                        setDateRange([dayjs().startOf('week'), dayjs().endOf('week')]);
                        setDatePopoverOpen(false);
                      }}
                    >
                      {t('crm.thisWeek', 'Цей тиждень')}
                    </button>
                    <button
                      type="button"
                      className={`crm-preset-btn ${datePreset === 'this_month' ? 'active' : ''}`}
                      onClick={() => {
                        setDatePreset('this_month');
                        setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
                        setDatePopoverOpen(false);
                      }}
                    >
                      {t('crm.thisMonth', 'Цей місяць')}
                    </button>
                    <button
                      type="button"
                      className={`crm-preset-btn ${datePreset === 'last_7_days' ? 'active' : ''}`}
                      onClick={() => {
                        setDatePreset('last_7_days');
                        setDateRange([dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')]);
                        setDatePopoverOpen(false);
                      }}
                    >
                      {t('crm.last7Days', 'Останні 7 днів')}
                    </button>
                  </div>
                  <div className="crm-date-custom-picker">
                    <div className="crm-date-custom-title">{t('crm.customRange', 'Власний діапазон')}:</div>
                    <RangePicker
                      value={dateRange}
                      onChange={(dates) => {
                        setDateRange(dates as [Dayjs | null, Dayjs | null]);
                        setDatePreset('custom');
                        if (dates && dates[0] && dates[1]) {
                          setDatePopoverOpen(false);
                        }
                      }}
                      format="DD/MM/YYYY"
                      allowEmpty={[true, true]}
                    />
                  </div>
                </div>
              }
            >
              <button
                type="button"
                className={`crm-filter-pill-btn ${
                  dateRange && (dateRange[0] || dateRange[1]) ? 'crm-pill-active' : ''
                }`}
              >
                <CalendarOutlined style={{ color: dateRange ? '#2563eb' : '#64748b' }} />
                <span>{datePillLabel}</span>
                <DownOutlined
                  style={{
                    fontSize: 10,
                    color: dateRange ? '#2563eb' : '#94a3b8',
                    marginLeft: 2,
                  }}
                />
              </button>
            </Popover>
          </div>

          <div className="crm-toolbar-right-group">
            {/* View Mode Switcher */}
            <div className="crm-view-mode-switch">
              <button
                type="button"
                className="crm-view-mode-btn active"
                title={t('crm.table', 'Таблиця')}
              >
                <UnorderedListOutlined />
                <span>{t('crm.table', 'Таблиця')}</span>
              </button>
              <button
                type="button"
                className="crm-view-mode-btn"
                onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/calendar`)}
                title={t('Calendar')}
              >
                <CalendarOutlined />
                <span>{t('Calendar')}</span>
              </button>
            </div>

            {/* Add Event Primary Action */}
            <Button
              type="primary"
              className="crm-primary-btn"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/event/new`)}
            >
              {t('AddCrmEvent')}
            </Button>
          </div>
        </div>

        {/* Quick Status Pill Tabs Row */}
        <div className="crm-status-tabs-row">
          <div className="crm-status-tabs">
            <button
              type="button"
              className={`crm-status-tab ${quickStatusTab === 'all' ? 'active' : ''}`}
              onClick={() => setQuickStatusTab('all')}
            >
              {t('crm.allEvents', 'Всі події')}{' '}
              <span className="crm-status-count">{counts.all}</span>
            </button>
            <button
              type="button"
              className={`crm-status-tab ${quickStatusTab === 'upcoming' ? 'active' : ''}`}
              onClick={() => setQuickStatusTab('upcoming')}
            >
              {t('crm.upcomingEvents', 'Майбутні')}{' '}
              <span className="crm-status-count">{counts.upcoming}</span>
            </button>
            <button
              type="button"
              className={`crm-status-tab ${quickStatusTab === 'today' ? 'active' : ''}`}
              onClick={() => setQuickStatusTab('today')}
            >
              {t('crm.todayEvents', 'Сьогодні')}{' '}
              <span className="crm-status-count">{counts.today}</span>
            </button>
            <button
              type="button"
              className={`crm-status-tab ${quickStatusTab === 'past' ? 'active' : ''}`}
              onClick={() => setQuickStatusTab('past')}
            >
              {t('crm.pastEvents', 'Завершені')}{' '}
              <span className="crm-status-count">{counts.past}</span>
            </button>
          </div>

          <div className="crm-gmail-counter">
            <span>
              {rangeStart}–{rangeEnd} {t('crm.of', 'із')} {filteredEvents.length}
            </span>
            {filteredEvents.length !== crmStore.CrmEvents.length && (
              <span className="crm-gmail-counter-secondary">
                ({t('crm.total', 'всього')} {crmStore.CrmEvents.length})
              </span>
            )}
          </div>
        </div>

        {/* Active Filter Chips Bar (appears when any filter is active) */}
        {hasActiveFilters && (
          <div className="crm-active-chips-row">
            <div className="crm-active-chips-label">
              <FilterOutlined style={{ fontSize: 12, color: '#64748b' }} />
              <span>{t('crm.activeFilters', 'Застосовані фільтри:')}</span>
            </div>
            <div className="crm-active-chips-list">
              {search && (
                <Tag closable onClose={() => setSearch('')} className="crm-filter-chip">
                  {t('Search')}: <b>{search}</b>
                </Tag>
              )}
              {selectedTypes.map((id) => {
                const name = crmStore.CrmEventTypes.find((t) => t.id === id)?.name || id;
                return (
                  <Tag
                    key={`type-${id}`}
                    closable
                    onClose={() => setSelectedTypes(selectedTypes.filter((x) => x !== id))}
                    className="crm-filter-chip"
                  >
                    {t('CrmEventType')}: <b>{name}</b>
                  </Tag>
                );
              })}
              {selectedContacts.map((id) => {
                const c = crmStore.contacts.find((x) => x.id === id);
                const name = c ? `${c.name} ${c.surname}`.trim() : id;
                return (
                  <Tag
                    key={`contact-${id}`}
                    closable
                    onClose={() => setSelectedContacts(selectedContacts.filter((x) => x !== id))}
                    className="crm-filter-chip"
                  >
                    {t('Contact')}: <b>{name}</b>
                  </Tag>
                );
              })}
              {selectedCompanies.map((id) => {
                const comp = crmStore.externalCompanies.find((x) => x.id === id);
                const name = comp?.name || id;
                return (
                  <Tag
                    key={`company-${id}`}
                    closable
                    onClose={() => setSelectedCompanies(selectedCompanies.filter((x) => x !== id))}
                    className="crm-filter-chip"
                  >
                    {t('Company')}: <b>{name}</b>
                  </Tag>
                );
              })}
              {dateRange && (dateRange[0] || dateRange[1]) && (
                <Tag
                  closable
                  onClose={() => {
                    setDateRange(null);
                    setDatePreset('all');
                  }}
                  className="crm-filter-chip"
                >
                  {t('crm.period', 'Період')}:{' '}
                  <b>
                    {dateRange[0]?.format('DD.MM.YYYY') || '...'} —{' '}
                    {dateRange[1]?.format('DD.MM.YYYY') || '...'}
                  </b>
                </Tag>
              )}
              {quickStatusTab !== 'all' && (
                <Tag closable onClose={() => setQuickStatusTab('all')} className="crm-filter-chip">
                  Статус:{' '}
                  <b>
                    {quickStatusTab === 'upcoming'
                      ? t('crm.upcomingEvents', 'Майбутні')
                      : quickStatusTab === 'today'
                      ? t('crm.todayEvents', 'Сьогодні')
                      : t('crm.pastEvents', 'Завершені')}
                  </b>
                </Tag>
              )}

              <Button
                type="link"
                size="small"
                className="crm-clear-all-btn"
                onClick={resetAllFilters}
              >
                <ReloadOutlined style={{ fontSize: 11 }} /> {t('crm.clearAll', 'Скинути все')}
              </Button>
            </div>
          </div>
        )}

        <Table
          className="crm-table"
          dataSource={filteredEvents}
          columns={columns}
          rowKey="id"
          rowClassName={() => 'crm-row-clickable'}
          onRow={(row: any) => ({
            onClick: () => navigate(`/${applicationStore.company?.Tenant}/crm/event/${row.id}`),
          })}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            },
          }}
          expandable={{
            expandedRowRender: (r) => (
              <div style={{ padding: 16 }}>
                <Typography.Text strong>{t('Description')}:</Typography.Text>
                <Typography.Paragraph>{r.description || t('NoDescription')}</Typography.Paragraph>
              </div>
            ),
          }}
        />
      </Card>
    </Spin>
  );
});

export default CrmEventManagement;
